import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { useState } from "react";
import { RiDownloadLine, RiExternalLinkLine } from "react-icons/ri";
import type { SitemapArgs } from "remix-sitemap";
import { serverOnly$ } from "vite-env-only/macros";
import type { CharacteristicRangeValue } from "~types/api/characteristic.types";
import type { Product, ProductRangeCompatible } from "~types/api/product.types";

import alternativeImage from "~/assets/images/fallback/main-image-fallback.png";
import modulesImg from "~/assets/images/pages/multiple-pages/solar-farm-1200-600.webp";
import { CertificateCard } from "~/components/CertificateCard";
import { ErrorComponent, NotFoundPage } from "~/components/error-boundary";
import { HtmlParser } from "~/components/HtmlParser";
import { SideMenuLayout, SideMenuLayoutWrapper } from "~/components/SideMenu/SideMenu";
import { SupportCard } from "~/components/SupportCard";
import { Typo } from "~/components/Typo";
import { Button } from "~/components/ui/button";
import { Listbox } from "~/components/ui/forms/listbox";
import { PageContainer } from "~/components/ui/layout/page-container";
import { PageHeading } from "~/components/ui/page-heading";
import { Heading } from "~/components/ui/typography";
import { staticCharacteristicsData } from "~/config/characteristics";
import { categoriesWithRanges } from "~/config/navigation";
import { getCategoryBySlug } from "~/models/category.server";
import { getCharacteristicRangeValues } from "~/models/characteristic.server";
import { getProductsCompatibleWithRange } from "~/models/product.server";
import { getRangeBySlug } from "~/models/range.server";
import { getSitemapRanges } from "~/models/sitemap.server";
import styles from "~/styles/RangePage.css?url";
import { NotFoundInApiError, NotFoundPageError } from "~/utils/errors";
import { handleError } from "~/utils/errors.server";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = serverOnly$(async ({ request }: SitemapArgs) => {
  const ranges = await getSitemapRanges({ request });
  if (!ranges) {
    return [];
  }
  return ranges.map((range) => ({
    loc: `/systemes-de-fixation/gamme/${range.slug}`,
    exclude: !range,
  }));
});

export const meta: MetaFunction<typeof loader> = (metaArgs) => {
  const data = metaArgs.data;
  if (!data?.range) {
    return [];
  }
  return getSeoMeta(metaArgs, {
    title: `Gamme ${data.range.brandRange.title || ""} - Systèmes de fixation`,
    description: `Retrouvez la gamme de fixation ${data.range.brandRange.title || ""} sur POwR Connect`,
  });
};

export const links: LinksFunction = () => {
  return [{ rel: "stylesheet", href: styles }];
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  try {
    const categorySlug = params["category"] ?? null;

    if (categorySlug && !categoriesWithRanges.includes(categorySlug)) {
      return redirect(`/${categorySlug}`, { status: 302 });
    }

    const [category, range, modulesCompatibles, characteristicRangeValues] = await Promise.all([
      getCategoryBySlug({ slug: categorySlug, request }),
      getRangeBySlug({ slug: params.range, request }).catch((error) => {
        if (error instanceof NotFoundInApiError) {
          throw new NotFoundPageError(`Aucune gamme ne correspond à cette url.`);
        }
        throw error;
      }),
      getProductsCompatibleWithRange({
        rangeSlug: params.range,
        // brandSlug: null,
        itemsPerPage: 1500,
        request,
      }),
      getCharacteristicRangeValues({ rangeSlug: params.range, value: "1", request }),
    ]);

    return json({
      category,
      modulesCompatibles,
      range,
      characteristicRangeValues,
    });
  } catch (error) {
    return handleError(error, {
      category: null,
      modulesCompatibles: null,
      range: null,
      characteristicRangeValues: null,
    });
  }
};

const RangePage = () => {
  const { category, modulesCompatibles, range, characteristicRangeValues } =
    useLoaderData<typeof loader>();

  const [modulesList, setModulesList] = useState<ProductRangeCompatible[]>(
    modulesCompatibles || []
  );
  const [selectedModuleBrand, setSelectedModuleBrand] = useState<string | null>(null);
  const [selectedModuleProduct, setSelectedModuleProduct] = useState<Product | null>(null);

  // filter modules by brand and remove duplicates brands
  const modulesBrands = modulesCompatibles
    ?.map((module) => module.product.brand)
    .filter((brand, index, self) => self.findIndex((t) => t.slug === brand.slug) === index);

  // Breadcrumbs
  const breadcrumbData = [
    {
      name: category?.title || "",
      slug: category?.slug ? `/${category.slug}/gammes` : "",
    },
    {
      name: range?.brand.title || "",
      slug:
        category?.slug && range?.brand.slug
          ? `/${category.slug}/gammes?marques=${range.brand.slug}`
          : "",
    },
    { name: range?.brandRange.title || "" },
  ];

  const supportsCategoriesData = staticCharacteristicsData.supportTypes.characteristics;

  const getCurrentRangeSupports = (characteristicRangeValues: CharacteristicRangeValue[]) => {
    return supportsCategoriesData
      .map((supportCategory) => {
        const category = {
          title: supportCategory.title,
          image: null,
          category: supportCategory.slug,
        };
        const supports = supportCategory.supports
          .filter(
            (support) => characteristicRangeValues.findIndex((v) => v.slug === support.slug) !== -1
          )
          .map((support) => ({
            title: support.title,
            image: support.image,
            category: null,
          }));
        if (supports.length === 0) {
          return null;
        }
        return [category, ...supports];
      })
      .filter((support) => support !== null);
  };

  const rangeSupports = getCurrentRangeSupports(characteristicRangeValues || []);

  if (!range) {
    return (
      <Heading as="h2" className="text-center">
        Les informations sur cette gamme ne sont pas disponibles
      </Heading>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        breadcrumbData={breadcrumbData}
        headingText={range.brand.title || ""}
        headingTextSecondary={range.brandRange.title || ""}
      />

      <SideMenuLayout
        productsPageSlug={`/systemes-de-fixation?gammes=${range.slug}`}
        websiteUrl={range.websiteUrl || null}
        computingToolUrl={range.computingToolUrl || null}
      >
        <SideMenuLayoutWrapper>
          {range.description ? (
            <section className="RangePage-section">
              <HtmlParser html={range.description} className="RangePage-description" />
            </section>
          ) : null}

          {rangeSupports && Array.isArray(rangeSupports) && rangeSupports.length > 0 ? (
            <section className="RangePage-section !mr-0">
              <Heading as="h2" size="2xl" className="mb-6">
                La gamme {range.brandRange.title} est spécialisée dans les fixations suivantes
              </Heading>
              <div className="RangePage-spec-cards">
                {rangeSupports.map((category, i) => {
                  if (!category || !Array.isArray(category) || category.length === 0) {
                    return null;
                  }
                  return (
                    <div key={`${category[0]?.title}-cat`} className="RangePage-spec-cards-group">
                      {category.map((support) => (
                        <SupportCard
                          key={`${support.title}-sup`}
                          className="RangePage-spec-cards-group-item"
                          flat={true}
                          title={support.title}
                          image={support.image}
                          category={support.category}
                        />
                      ))}
                      {i !== rangeSupports.length - 1 ? (
                        <div className="RangePage-spec-cards-group-separator" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {range.readMore ? (
            <section className="RangePage-section">
              <Heading as="h2" size="2xl" className="mb-6">
                En savoir plus sur la gamme {range.brandRange.title}
              </Heading>
              <HtmlParser html={range.readMore} className="RangePage-readmore" />
            </section>
          ) : null}

          {range.useCase ||
          range.cover ||
          range.moduleOrientation ||
          range.systemWeight ||
          range.supportingStructure ? (
            <section className="RangePage-section">
              <Heading as="h2" size="2xl" className="mb-6">
                Caractéristiques techniques
              </Heading>
              <div className="RangePage-list">
                {range.useCase ? (
                  <>
                    <Typo fontColor="grey-900" fontWeight="semibold">
                      Utilisation :
                    </Typo>
                    <Typo fontColor="grey-900" fontSize="sm">
                      {range.useCase}
                    </Typo>
                  </>
                ) : null}
                {range.cover ? (
                  <>
                    <Typo fontColor="grey-900" fontWeight="semibold">
                      Couverture :
                    </Typo>
                    <Typo fontColor="grey-900" fontSize="sm">
                      {range.cover}
                    </Typo>
                  </>
                ) : null}
                {range.moduleOrientation ? (
                  <>
                    <Typo fontColor="grey-900" fontWeight="semibold">
                      Orientation du module :
                    </Typo>
                    <Typo fontColor="grey-900" fontSize="sm">
                      {range.moduleOrientation}
                    </Typo>
                  </>
                ) : null}
                {range.systemWeight ? (
                  <>
                    <Typo fontColor="grey-900" fontWeight="semibold">
                      Poids du système :
                    </Typo>
                    <Typo fontColor="grey-900" fontSize="sm">
                      {range.systemWeight}
                    </Typo>
                  </>
                ) : null}
                {range.supportingStructure ? (
                  <>
                    <Typo fontColor="grey-900" fontWeight="semibold">
                      Structure porteuse :
                    </Typo>
                    <Typo fontColor="grey-900" fontSize="sm">
                      {range.supportingStructure}
                    </Typo>
                  </>
                ) : null}
              </div>
            </section>
          ) : null}

          {range.reinsurance ? (
            <section className="RangePage-section">
              <Heading as="h2" size="2xl" className="mb-6">
                Acheter des produits de la gamme {range.brandRange.title} c&apos;est s&apos;assurer
              </Heading>
              <HtmlParser html={range.reinsurance} className="RangePage-reinsurance" />
            </section>
          ) : null}
        </SideMenuLayoutWrapper>

        {range.certifications &&
        Array.isArray(range.certifications) &&
        range.certifications.length > 0 ? (
          <section className="RangePage-section !mb-14 !bg-grey-100 !py-14">
            <SideMenuLayoutWrapper>
              <Heading as="h2" size="2xl" className="mb-6">
                Les certifications pour la gamme {range.brandRange.title}
              </Heading>
              <div className="RangePage-certifications">
                {range.certifications.map((certification) => (
                  <CertificateCard
                    certification={certification}
                    key={certification.id}
                    className="RangePage-certifications-item"
                  />
                ))}
              </div>
            </SideMenuLayoutWrapper>
          </section>
        ) : null}

        {range.rangeDocuments.length > 0 || range.links.length > 0 ? (
          <SideMenuLayoutWrapper className="RangePage-section grid grid-cols-1 gap-12 md:grid-cols-2 lg:!pr-80">
            {range.rangeDocuments &&
            Array.isArray(range.rangeDocuments) &&
            range.rangeDocuments.length > 0 ? (
              <section id="downloads">
                <Heading as="h2" size="2xl" className="mb-6">
                  Documents à télécharger
                </Heading>
                {range.rangeDocuments.map((doc) => (
                  <Link
                    to={doc.url}
                    className="RangePage-document"
                    key={doc.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <RiDownloadLine className="RangePage-document-icon shrink-0" />
                    <span>{doc.title || doc.filename}</span>
                  </Link>
                ))}
              </section>
            ) : null}
            {range.links && Array.isArray(range.links) && range.links.length > 0 ? (
              <section>
                <Heading as="h2" size="2xl" className="mb-6">
                  Autres liens
                </Heading>
                {range.links.map((link) => (
                  <Link
                    to={link.url}
                    className="RangePage-document"
                    key={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <RiExternalLinkLine className="RangePage-document-icon shrink-0" />
                    <span>{link.title}</span>
                  </Link>
                ))}
              </section>
            ) : null}
          </SideMenuLayoutWrapper>
        ) : null}

        {modulesCompatibles && modulesCompatibles.length > 0 ? (
          <section className="RangePage-section RangePage-searchModules !mb-14 !bg-grey-100 !py-14">
            <SideMenuLayoutWrapper>
              <div className="RangePage-searchModules-header">
                <img
                  src={modulesImg}
                  alt="Panneaux solaires"
                  className="RangePage-searchModules-header-image"
                />
                <Heading as="h2" size="2xl" className="mb-6">
                  Recherchez des modules photovoltaïques certifiés avec la gamme{" "}
                  {range.brandRange.title}
                </Heading>
              </div>
              <div className="RangePage-searchModules-form">
                <Listbox
                  options={[
                    { id: "", label: "Toutes les marques de modules", value: "" },
                    ...(modulesBrands?.map((brand) => ({
                      id: brand.id,
                      label: brand.title,
                      value: brand.slug,
                    })) || []),
                  ]}
                  onChange={(option: { value: string }) => {
                    if (option.value && option.value !== "") {
                      setSelectedModuleBrand(option.value);
                      setModulesList(
                        modulesCompatibles.filter(
                          (module) => module.product.brand.slug === option.value
                        ) || []
                      );
                    } else {
                      setSelectedModuleBrand(null);
                      setModulesList(modulesCompatibles || []);
                    }
                    return null;
                  }}
                  className="RangePage-searchModules-form-brand"
                />

                <Listbox
                  options={[
                    { id: "", label: "Tous les modules", value: "" },
                    ...(modulesList.map((compatibleModule) => ({
                      id: compatibleModule.product.id,
                      label: (
                        <>
                          <img
                            className="RangePage-searchModules-form-module-option-image"
                            src={compatibleModule.product.images[0]?.url || alternativeImage}
                            alt={compatibleModule.product.title}
                          />
                          <div className="RangePage-searchModules-form-module-option-content">
                            <div className="RangePage-searchModules-form-module-option-title">
                              {compatibleModule.product.title}
                            </div>
                            <div className="RangePage-searchModules-form-module-option-ref">
                              {compatibleModule.product.reference}
                            </div>
                          </div>
                        </>
                      ),
                      labelInInput: compatibleModule.product.title,
                      value: compatibleModule.product,
                    })) || []),
                  ]}
                  onChange={(option: { value: Product }) => {
                    if (option.value) {
                      setSelectedModuleProduct(option.value);
                    } else {
                      setSelectedModuleProduct(null);
                    }
                    return null;
                  }}
                  className="RangePage-searchModules-form-module"
                />
                <Button
                  className="RangePage-searchModules-form-button"
                  as="link"
                  to={
                    !selectedModuleBrand
                      ? `/recherche?gamme-compatible=${range.slug}`
                      : !selectedModuleProduct && selectedModuleBrand
                        ? `/recherche?gamme-compatible=${range.slug}&marques=${selectedModuleBrand}`
                        : `/${selectedModuleProduct?.categories[0]?.slug}/${selectedModuleProduct?.slug}`
                  }
                  disabled={!(modulesCompatibles.length > 0)}
                >
                  Rechercher
                </Button>
              </div>
            </SideMenuLayoutWrapper>
          </section>
        ) : null}

        <section className="RangePage-section RangePage-brand">
          <SideMenuLayoutWrapper>
            <Heading as="h2" size="2xl" className="mb-6">
              {range.brand.title}
            </Heading>
            <div className="RangePage-brand-flex">
              <div className="RangePage-brand-leftCol">
                {range.brand.description ? (
                  <HtmlParser
                    html={range.brand.description}
                    className="RangePage-brand-description"
                  />
                ) : null}

                {range.brand.presentation && range.brand.presentation.length > 0 ? (
                  <div className="mt-4 flex items-center justify-end">
                    <Link
                      to={`/nos-partenaires/${range.brand.slug}`}
                      className="font-medium underline"
                    >
                      En savoir +
                    </Link>
                  </div>
                ) : null}
              </div>

              {range.brand.presentation && range.brand.presentation.length > 0 ? (
                <Link to={`/nos-partenaires/${range.brand.slug}`}>
                  <img
                    className="RangePage-brand-image"
                    src={range.brand.images[0]?.url}
                    alt="Logo marque"
                  />
                </Link>
              ) : (
                <img
                  className="RangePage-brand-image"
                  src={range.brand.images[0]?.url}
                  alt="Logo marque"
                />
              )}
            </div>
          </SideMenuLayoutWrapper>
        </section>
      </SideMenuLayout>
    </PageContainer>
  );
};

export default RangePage;

export const ErrorBoundary = () => {
  return (
    <ErrorComponent
      statusHandlers={{
        404: ({ error }) => <NotFoundPage>{error.data}</NotFoundPage>,
      }}
    />
  );
};
